import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { differenceInDays } from "date-fns";
import { defaultStaticRanges } from "../../config/analytics/StaticDateRanges";
import AppContext from "../../state/AppContext";
import {
  AnalyticsWidgetUiDetailsDto,
  FilterAvailableValues,
  JourneyFilterDefaultValueMode,
} from "../../types/analytics";
import { GroupedJourneys } from "../../types/analytics/FilterAvailableValues";
import FilterDefaultSelectedValues from "../../types/analytics/FilterDefaultSelectedValues";
import AnalyticsFilterInputs, {
  AnalyticsFilterComparisonProperty,
  AnalyticsPageJourneyFilterRestriction,
} from "../../types/analytics/AnalyticsFilterInputs";
import { IDateRange } from "../../types/generic";
import AnalyticsFilters from "./AnalyticsFilters";
import analyticsFiltersApi from "../../api/analytics/analyticsFiltersApi";
import IAnalyticsPageApi from "../../types/analytics/IAnalyticsPageApi";
import { AnalyticsPageDataDto } from "../../types/analytics/AnalyticsPageDataDto";
import AnalyticsWidgetCollection from "./AnalyticsWidgetCollection";
import AnalyticsPageLoadError from "./AnalyticsPageLoadError";
import { AnalyticsLoadingSpinner } from "./AnalyticsLoadingSpinner";
import { AnalyticsFilterBandSkeleton } from "./AnalyticsFilterBandSkeleton";
import {
  useAppInsightsContext,
  useTrackEvent,
} from "@microsoft/applicationinsights-react-js";
import { AnalyticsWidgetRefreshArguments } from "../../types/analytics/AnalyticsWidgetRefreshFilterParams";
import { FilterAvailableValuesDto } from "../../types/analytics/FilterAvailableValues";
import { useSessionStorage } from "react-use";
import UserContext from "../../state/UserContext";
import StoredAnalyticFilters from "../../types/analytics/StoredAnalyticFilters";
import { faChartLineUp } from "@fortawesome/pro-regular-svg-icons";
import DynamicAnalyticsEditorMenu from "../../routes/analytics/admin/DynamicAnalyticsEditorMenu";
import { analyticsFiltersAreDirty, sameDateRange } from "../../helpers/analytics/analyticsFilterBarHelpers";
import { pageSupportsPointInTime } from "../../helpers/analytics/pointInTimeFilterHelpers";
import { useCheckMobileScreen } from "../../hooks";
import permissionHelper from "../../helpers/permissionHelper";
import AnalyticsFiltersWalkthrough from "../user-walkthrough/AnalyticsFiltersWalkthrough";

interface AnalyticsPageProps {
  /** A unique identifier for this page, triggers a reload of data on change */
  pageIdentifier: string;
  /** If this page is a dynamic (database-loaded widgets) page, provide the AnaltyicsPageId */
  dynamicPageId?: number | undefined;
  title: string;
  translateTitle?: boolean;
  children?: React.ReactNode | undefined;
  journeyFilterDefaultValueMode: JourneyFilterDefaultValueMode;
  journeyFilterRestriction: AnalyticsPageJourneyFilterRestriction;
  /** The API to load data from when the filters change, or the page first loads */
  analyticsPageApi?: IAnalyticsPageApi | undefined;
  /** Whether widgets will be loaded, or something different */
  contentType: "WIDGETS" | "CUSTOM";
  defaultJourneyFilterPattern?: string | null;
  /** Receives the loaded data along with the filters it was loaded for */
  onPageDataChanged?: (
    data: AnalyticsPageDataDto | null,
    appliedFilters: AnalyticsFilterInputs,
    context?: AnalyticsPageLoadContext,
  ) => void;
  childrenAfterFirstWidget?: boolean;
  /** When true, widget cards are not rendered; children supply the page layout instead */
  suppressDefaultWidgets?: boolean;
  /**
   * Fired when the applied Show past data date changes so hosts can re-load
   * page chrome (title / journey restriction) AS OF that date (ADR-0006).
   */
  onAppliedPointInTimeDateChange?: (pointInTimeDate: string | null) => void;
}

export interface AnalyticsPageLoadContext {
  widgets: AnalyticsWidgetUiDetailsDto[];
  journeys: GroupedJourneys[];
}

function AnalyticsPage({
  pageIdentifier,
  dynamicPageId = undefined,
  title,
  translateTitle = true,
  journeyFilterDefaultValueMode,
  journeyFilterRestriction,
  contentType,
  analyticsPageApi = undefined,
  children = undefined,
  defaultJourneyFilterPattern,
  onPageDataChanged,
  childrenAfterFirstWidget = false,
  suppressDefaultWidgets = false,
  onAppliedPointInTimeDateChange,
}: AnalyticsPageProps) {
  const { t } = useTranslation();
  const appContext = useContext(AppContext);
  const userContext = useContext(UserContext);
  const auth = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const shareSavedFilterId = useMemo(() => {
    const raw = searchParams.get("sf");
    if (!raw) return null;
    const id = Number.parseInt(raw, 10);
    return Number.isFinite(id) ? id : null;
  }, [searchParams]);
  const [shareFilterLoadFailed, setShareFilterLoadFailed] = useState(false);
  const filtersApi = new analyticsFiltersApi(auth.user?.access_token);
  const appInsights = useAppInsightsContext();
  const isMobile = useCheckMobileScreen();
  const [allowWalkthrough, setAllowWalkthrough] = useState(true);
  const canManageSharedAnalyticsFilters =
    permissionHelper.userHasEitherPermission(
      userContext,
      "ManageSharedAnalyticsFilters",
      "MANAGE-SHARED-ANALYTICS-FILTERS",
    );

  useEffect(() => {
    if (contentType !== "WIDGETS") {
      return;
    }
    appContext.setActiveDashboardTypeForWalkthrough("ANALYTICS-FILTERS");
    return () => {
      appContext.setActiveDashboardTypeForWalkthrough(null);
    };
  }, [contentType, pageIdentifier]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWalkthroughFinished = () => {
    setAllowWalkthrough(false);
  };

  // ------------ Filter-related code ------------
  function filtersHaveBeenSet() {
    return filterAppliedValues && !!filterAppliedValues.dateRange;
  }

  const reloadGenerationRef = useRef(0);

  /** Call the API to refresh the page data */
  function reloadData() {
    // Don't call the API if we're not ready
    if (
      !analyticsPageApi ||
      !filtersReady ||
      !filtersHaveBeenSet() ||
      contentType === "CUSTOM"
    )
      return;

    const requestGeneration = ++reloadGenerationRef.current;
    const filtersForRequest = filterAppliedValues;

    const onApiSuccess = (data: AnalyticsPageDataDto | null) => {
      if (requestGeneration !== reloadGenerationRef.current) return;

      if (!data) {
        setWidgets([]);
      } else {
        setWidgets(data.widgets);
      }
      onPageDataChanged?.(data, filtersForRequest, {
        widgets: data?.widgets ?? [],
        journeys: filterAvailableValues.journeys,
      });
      setPageDataError(false);
      setWidgetsAreLoading(false);
    };

    const onApiError = (err: any) => {
      if (requestGeneration !== reloadGenerationRef.current) return;

      console.error("load page data error", err);
      setPageDataError(true);
      setWidgetsAreLoading(false);
    };

    setPageDataError(false);
    setWidgetsAreLoading(true);

    analyticsPageApi.getPageData(filtersForRequest, onApiSuccess, onApiError);
  }

  const emptyFilterSelection: AnalyticsFilterInputs = {
    dateRange: null,
    allJourneys: false,
    journeyKeys: [],
    activeUsersOnly: true,
    includePartiallyApproved: false,
    comparisonProperty: "NONE",
    locationIds: [],
    appraisalLevelIds: [],
    jobTitleIds: [],
    userGroupIds: [],
  };

  // The options available in the filters
  const [filterAvailableValues, setFilterAvailableValues] =
    useState<FilterAvailableValues>({
      definedDateRanges: [],
      journeys: [],
      defaultJourney: null,
    });

  // These are the values selected in the filters UI, but not necessarily applied yet
  const [filterSelectedValues, setFilterSelectedValues] =
    useState<AnalyticsFilterInputs>(emptyFilterSelection);

  // These are the values applied, which the data being shown is loaded for
  const [filterAppliedValues, setFilterAppliedValues] =
    useState<AnalyticsFilterInputs>(emptyFilterSelection);

  useEffect(() => {
    onAppliedPointInTimeDateChange?.(
      filterAppliedValues.pointInTimeDate?.trim() || null
    );
  }, [filterAppliedValues.pointInTimeDate, onAppliedPointInTimeDateChange]);

  const [appliedSavedFilterId, setAppliedSavedFilterId] = useState<
    number | null
  >(null);

  const [storedAnalyticFilters, setStoredAnalyticFilters] =
    useSessionStorage<StoredAnalyticFilters | null>(
      "Analytics-StoredFilters",
      null
    );
  /**
   * Opt-in cross-page continuity for this browser tab only. When off (default),
   * each page landing is page-local (pin or built-in) and we never write the
   * session blob - otherwise leftover audience from another page would skip pins.
   */
  const [carryFiltersAcrossPages, setCarryFiltersAcrossPages] =
    useSessionStorage<boolean>("Analytics-RememberFilters", false);

  const carryFiltersRef = useRef(carryFiltersAcrossPages);
  carryFiltersRef.current = carryFiltersAcrossPages;
  const filterAppliedValuesRef = useRef(filterAppliedValues);
  filterAppliedValuesRef.current = filterAppliedValues;
  const appliedSavedFilterIdRef = useRef(appliedSavedFilterId);
  appliedSavedFilterIdRef.current = appliedSavedFilterId;

  // Clear the stored filters if they were stored by a different user
  if (
    storedAnalyticFilters != null &&
    storedAnalyticFilters.storedByUserId !== userContext.user?.id
  ) {
    setStoredAnalyticFilters(null);
  }

  /**
   * Rollout + Keep-off invariant: ignore/clear legacy blobs unless the carry pin is on.
   * Existing tabs may still have Analytics-StoredFilters from the old always-carry model.
   */
  useEffect(() => {
    if (!carryFiltersAcrossPages && storedAnalyticFilters != null) {
      setStoredAnalyticFilters(null);
    }
  }, [carryFiltersAcrossPages, storedAnalyticFilters, setStoredAnalyticFilters]);

  /** Persist live-bar state only while the carry pin is on (values + attached saved id). */
  const persistCarriedFilters = (
    filters: AnalyticsFilterInputs,
    savedFilterId: number | null,
  ) => {
    if (!carryFiltersRef.current) {
      return;
    }
    setStoredAnalyticFilters({
      filters,
      storedByUserId: userContext.user?.id || 0,
      appliedSavedFilterId: savedFilterId,
    });
  };

  // Whether or not the filters are ready to use for querying the API with
  const [filtersReady, setFiltersReady] = useState<boolean>(false);
  /**
   * False until share (`?sf=`) / page-default saved-filter resolve finishes (or is
   * not needed). Prevents painting and querying with built-in defaults first.
   */
  const [savedFilterBootstrapDone, setSavedFilterBootstrapDone] =
    useState(false);
  /** Invalidates in-flight saved-filter bootstrap when the page or catalogue changes. */
  const savedFilterBootstrapGenerationRef = useRef(0);
  const [savedFilterBootstrapGeneration, setSavedFilterBootstrapGeneration] =
    useState(0);
  /** Drops stale getAvailableFilterValues responses after navigating away. */
  const filterCatalogueLoadGenerationRef = useRef(0);

  const beginSavedFilterBootstrap = () => {
    const nextGeneration = savedFilterBootstrapGenerationRef.current + 1;
    savedFilterBootstrapGenerationRef.current = nextGeneration;
    setSavedFilterBootstrapGeneration(nextGeneration);
    setSavedFilterBootstrapDone(false);
    setFiltersReady(false);
    setWidgetsAreLoading(true);
  };

  const trackAnalyticsFilterChangeEvent = useTrackEvent(
    appInsights,
    "Analytics-FilterChange",
    {}
  );

  function loadAvailableFilterValues() {
    // Don't load filters for custom pages
    if (contentType === "CUSTOM") {
      return;
    }

    const loadGeneration = ++filterCatalogueLoadGenerationRef.current;
    setFilterCatalogueError(false);

    const onFilterLoadSuccess = (data: FilterAvailableValuesDto) => {
      if (loadGeneration !== filterCatalogueLoadGenerationRef.current) {
        return;
      }

      const apiAvailableValues = {
        ...data,
        definedDateRanges: defaultStaticRanges.map((sr) => {
          const range = sr.range();
          return {
            startDate: range.startDate,
            endDate: range.endDate,
            label: sr.label!,
          };
        }),
      };

      // Store the available values in the state so we don't need to load them again
      setFilterAvailableValues(apiAvailableValues);
      setFilterCatalogueError(false);

      // Set the default filter values
      setDefaultValuesForFilters(apiAvailableValues);
    };

    const onFilterLoadError = (error: any) => {
      if (loadGeneration !== filterCatalogueLoadGenerationRef.current) {
        return;
      }
      console.error("Error loading filters", error);
      setFilterCatalogueError(true);
    };

    filtersApi.getAvailableFilterValues(
      journeyFilterRestriction,
      defaultJourneyFilterPattern,
      onFilterLoadSuccess,
      onFilterLoadError
    );
  }

  function setDefaultValuesForFilters(
    apiAvailableValues: FilterAvailableValues,
    forceDefaultValues: boolean = false
  ) {
    // Get then set the default filter values
    const filterDefaultValues = new FilterDefaultSelectedValues(
      journeyFilterDefaultValueMode,
      apiAvailableValues
    );

    // Landing priority (when not force-reset):
    // ?sf= is resolved in AnalyticsFilters bootstrap after this; then
    // carry pin + session blob; else page pin (bootstrap); else built-in below.
    // Session is ignored unless the carry pin is on so carried audience cannot skip pins.
    const restoreCarriedSession =
      !forceDefaultValues &&
      carryFiltersAcrossPages &&
      storedAnalyticFilters != null;

    if (restoreCarriedSession) {
      const storedFilters: AnalyticsFilterInputs = {
        ...storedAnalyticFilters!.filters,
      };

      if (storedFilters.dateRange?.startDate) {
        storedFilters.dateRange = {
          ...storedFilters.dateRange,
          startDate: new Date(storedFilters.dateRange.startDate),
          endDate: storedFilters.dateRange.endDate
            ? new Date(storedFilters.dateRange.endDate)
            : storedFilters.dateRange.endDate,
        };
      }

      const availableKeys = new Set(
        apiAvailableValues.journeys.flatMap((g) => g.journeys.map((j) => j.key))
      );
      const keptKeys = (storedFilters.journeyKeys ?? []).filter((k) =>
        availableKeys.has(k)
      );

      if (keptKeys.length > 0) {
        storedFilters.journeyKeys = keptKeys;
        storedFilters.allJourneys =
          keptKeys.length === availableKeys.size && availableKeys.size > 0;
      } else {
        storedFilters.allJourneys = filterDefaultValues.allJourneys;
        storedFilters.journeyKeys = filterDefaultValues.journeyKeys;
      }

      // Usage (and any other non-PIT page) must never half-honour a carried date.
      if (!pageSupportsPointInTime(pageIdentifier)) {
        storedFilters.pointInTimeDate = null;
      }

      setFilterSelectedValues(storedFilters);
      setFilterAppliedValues(storedFilters);
      setAppliedSavedFilterId(
        storedAnalyticFilters!.appliedSavedFilterId ?? null,
      );
      // Still wait if ?sf= may override the remembered session.
      if (shareSavedFilterId != null) {
        beginSavedFilterBootstrap();
      } else {
        setSavedFilterBootstrapDone(true);
        setFiltersReady(true);
      }
    } else {
      setFilterSelectedValues(filterDefaultValues);
      setFilterAppliedValues(filterDefaultValues);
      setAppliedSavedFilterId(null);
      // Built-in defaults may be replaced by page-default pin or ?sf= in bootstrap.
      if (!forceDefaultValues) {
        beginSavedFilterBootstrap();
      } else {
        setSavedFilterBootstrapDone(true);
        setFiltersReady(true);
      }
    }
  }

  const handleJourneyPickerChange = (selectedJourneyKeys: string[]) => {
    // Check whether all items should be selected
    const allJourneysAreSelected =
      filterAvailableValues?.journeys.flatMap((x) => x.journeys).length ===
      selectedJourneyKeys.length;

    const newValues: AnalyticsFilterInputs = {
      ...filterSelectedValues,
      journeyKeys: selectedJourneyKeys,
      allJourneys: allJourneysAreSelected,
    };
    setFilterSelectedValues(newValues);
  };

  const handleUserGroupPickerChange = (selectedUserGroupIds: number[]) => {
    const newValues: AnalyticsFilterInputs = {
      ...filterSelectedValues,
      userGroupIds: selectedUserGroupIds,
    };
    setFilterSelectedValues(newValues);
  };

  const handleLocationPickerChange = (selectedLocationIds: number[]) => {
    const newValues: AnalyticsFilterInputs = {
      ...filterSelectedValues,
      locationIds: selectedLocationIds,
    };
    setFilterSelectedValues(newValues);
  };

  const handleJobTitlePickerChange = (selectedJobTitleIds: number[]) => {
    const newValues: AnalyticsFilterInputs = {
      ...filterSelectedValues,
      jobTitleIds: selectedJobTitleIds,
    };
    setFilterSelectedValues(newValues);
  };

  const handleAppraisalPickerChange = (selectedAppraisalIds: number[]) => {
    const newValues: AnalyticsFilterInputs = {
      ...filterSelectedValues,
      appraisalLevelIds: selectedAppraisalIds,
    };
    setFilterSelectedValues(newValues);
  };

  const handleApprovedDataOnlyChange = (approvedOnly: boolean) => {
    const newValues: AnalyticsFilterInputs = {
      ...filterSelectedValues,
      includePartiallyApproved: !approvedOnly,
    };
    setFilterSelectedValues(newValues);
  };

  const handleActiveOnlyChange = (includeLeavers: boolean) => {
    const newValues: AnalyticsFilterInputs = {
      ...filterSelectedValues,
      activeUsersOnly: !includeLeavers,
    };
    setFilterSelectedValues(newValues);
  };

  const handleComparePropertyChange = (
    newValue: AnalyticsFilterComparisonProperty
  ) => {
    const newValues: AnalyticsFilterInputs = {
      ...filterSelectedValues,
      comparisonProperty: newValue,
    };
    setFilterSelectedValues(newValues);
  };

  const handleJourneyPickerAllSelectedChange = (allSelected: boolean) => {
    const allJourneyKeys = filterAvailableValues
      ? filterAvailableValues.journeys
          .flatMap((x) => x.journeys)
          .map((x) => x.key)
      : [];

    const newValues: AnalyticsFilterInputs = {
      ...filterSelectedValues,
      allJourneys: allSelected,
      journeyKeys: allSelected ? allJourneyKeys : [],
    };
    setFilterSelectedValues(newValues);
  };

  const handleDateRangeChange = (newValue: IDateRange | null) => {
    const newValues: AnalyticsFilterInputs = {
      ...filterSelectedValues,
      dateRange: newValue,
    };
    setFilterSelectedValues(newValues);
  };

  /** Full staged replace — Advanced panel Apply commits its draft in one write. */
  const handleStageFilterInputs = (values: AnalyticsFilterInputs) => {
    setFilterSelectedValues(values);
  };

  /** All-activity resolve: keep staged + applied in sync so resolve alone is not "dirty". */
  const handleActivityWindowDateRangeChange = (newValue: IDateRange | null) => {
    // Same absolute window must not rewrite applied state - that would reload charts.
    setFilterSelectedValues((prev) => {
      if (sameDateRange(prev.dateRange, newValue)) return prev;
      return {
        ...prev,
        dateRange: newValue,
      };
    });
    setFilterAppliedValues((prev) => {
      if (sameDateRange(prev.dateRange, newValue)) return prev;
      const next = {
        ...prev,
        dateRange: newValue,
      };
      filterAppliedValuesRef.current = next;
      return next;
    });
  };

  const onApplyFilters = (values?: AnalyticsFilterInputs) => {
    const toApply = values ?? filterSelectedValues;
    filterAppliedValuesRef.current = toApply;
    appliedSavedFilterIdRef.current = null;
    setFilterAppliedValues(toApply);
    setFilterSelectedValues(toApply);
    // Apply detaches the named save (caller clears id); only write session when the carry pin is on.
    persistCarriedFilters(toApply, null);
    trackFilterUsage(toApply);
  };

  /**
   * Show past data is staged like any other filter (ADR-0006) - the charts keep
   * using the applied date until Apply.
   */
  const handlePointInTimeDateChange = (date: string | null) => {
    setFilterSelectedValues((prev) => ({
      ...prev,
      pointInTimeDate: date?.trim() || null,
    }));
  };

  const onDiscardFilters = () => {
    setFilterSelectedValues(filterAppliedValues);
  };

  const onRecallFilters = (
    values: AnalyticsFilterInputs,
    savedFilterId: number | null = null,
  ) => {
    filterAppliedValuesRef.current = values;
    appliedSavedFilterIdRef.current = savedFilterId;
    setFilterSelectedValues(values);
    setFilterAppliedValues(values);
    persistCarriedFilters(values, savedFilterId);
    trackFilterUsage(values);
  };

  /**
   * Revert / No saved filter: page-local reopen (built-in, then pin via bootstrap).
   * Carry pin stays as-is; clear blob so page default can apply, then re-seed after bootstrap.
   */
  const onResetFilters = () => {
    const filterDefaultValues = new FilterDefaultSelectedValues(
      journeyFilterDefaultValueMode,
      filterAvailableValues,
    );
    filterAppliedValuesRef.current = filterDefaultValues;
    appliedSavedFilterIdRef.current = null;
    setFilterSelectedValues(filterDefaultValues);
    setFilterAppliedValues(filterDefaultValues);
    setAppliedSavedFilterId(null);
    // Clear blob so applyPageDefaultOnLoad can run even when the carry pin is still on.
    setStoredAnalyticFilters(null);
    beginSavedFilterBootstrap();
  };

  /**
   * Turning the carry pin on: seed session from what the charts show (applied), including
   * any attached saved-filter id, so the next page carries that continuity.
   */
  const onCarryFiltersAcrossPagesChange = (enabled: boolean) => {
    if (enabled) {
      // Ref first so persistCarriedFilters sees the pin as on in this tick.
      carryFiltersRef.current = true;
      setCarryFiltersAcrossPages(true);
      persistCarriedFilters(
        filterAppliedValuesRef.current,
        appliedSavedFilterIdRef.current,
      );
      return;
    }

    // Turning off: abandon carry immediately - reset this page to pin/built-in.
    carryFiltersRef.current = false;
    setCarryFiltersAcrossPages(false);
    setStoredAnalyticFilters(null);
    const filterDefaultValues = new FilterDefaultSelectedValues(
      journeyFilterDefaultValueMode,
      filterAvailableValues,
    );
    filterAppliedValuesRef.current = filterDefaultValues;
    appliedSavedFilterIdRef.current = null;
    setFilterSelectedValues(filterDefaultValues);
    setFilterAppliedValues(filterDefaultValues);
    setAppliedSavedFilterId(null);
    beginSavedFilterBootstrap();
  };

  // ------------ Chart data related code ------------
  const [widgets, setWidgets] = useState<AnalyticsWidgetUiDetailsDto[]>([]);
  const [widgetsAreLoading, setWidgetsAreLoading] = useState<boolean>(true);
  const [pageDataError, setPageDataError] = useState(false);
  const [filterCatalogueError, setFilterCatalogueError] = useState(false);

  // ------------ Side effects ------------
  useEffect(() => {
    setPageTitle();
    appContext.setShowPageTitleAccent(true);
    // Flush filter band under #top-bar (compact title margins); restore on leave.
    appContext.setShowTopBarTitleSpacing(false);
    // Drop the previous page catalogue so AnalyticsFilters unmounts until this
    // page's available values + defaults are applied. Avoids saved-filter
    // bootstrap finishing before setDefaultValues and leaving the band stuck.
    beginSavedFilterBootstrap();
    setPageDataError(false);
    setFilterCatalogueError(false);
    setFilterAvailableValues({
      definedDateRanges: [],
      journeys: [],
      defaultJourney: null,
    });
    loadAvailableFilterValues();
    return () => {
      appContext.setShowTopBarTitleSpacing(true);
    };
  }, [pageIdentifier]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPageTitle();
  }, [title, translateTitle]);

  useEffect(() => {
    // Reload the data when the applied filters change
    reloadData();
  }, [filterAppliedValues, filtersReady, dynamicPageId]);

  /** Log how the user is using the filters so we can make product decisions with the data */
  const trackFilterUsage = (filterValues: AnalyticsFilterInputs) => {
    trackAnalyticsFilterChangeEvent({
      dateRange: getDateRangeDays(filterValues.dateRange),
      journeys: getFilterArrayPropertyLength(filterValues.journeyKeys),
      locations: getFilterArrayPropertyLength(filterValues.locationIds),
      jobTitles: getFilterArrayPropertyLength(filterValues.jobTitleIds),
      appraisalLevels: getFilterArrayPropertyLength(
        filterValues.appraisalLevelIds
      ),
      approvedOnly: filterValues.includePartiallyApproved,
      activeOnly: filterValues.activeUsersOnly,
      comparisonProperty: filterValues.comparisonProperty,
    });
  };

  const getDateRangeDays = (dateRange: IDateRange | null): number => {
    if (dateRange === null || !dateRange.startDate || !dateRange.endDate) {
      return 0;
    }
    return differenceInDays(dateRange.endDate, dateRange.startDate);
  };

  const getFilterArrayPropertyLength = (array: any[] | null): number => {
    return array !== null && array.length > 0 ? array.length : 0;
  };

  const setPageTitle = () => {
    appContext.setPageTitle(translateTitle ? t(title) : title);
    appContext.setPageIcon(faChartLineUp);
  };

  const handleOtherWidgetRefreshRequest = (
    dynamicWidgetId: number,
    onSuccess: () => void,
    onError: () => void
  ) => {
    if (!analyticsPageApi || !dynamicPageId) return;

    const successCallback = (data: AnalyticsWidgetUiDetailsDto) => {
      // Set the new widget data
      const newWidgetState = [...widgets];
      const widgetIndex = newWidgetState.findIndex(
        (x) => x.dynamicWidgetId === dynamicWidgetId
      );
      newWidgetState[widgetIndex] = data;
      setWidgets(newWidgetState);

      // Call the success callback
      onSuccess();
    };

    const errorCallback = (error: any) => {
      onError();
      console.error(error);
    };

    const refreshParams: AnalyticsWidgetRefreshArguments = {
      analyticsPageId: dynamicPageId,
      widgetId: dynamicWidgetId,
      pageNumber: null,
      pageSize: null,
      searchValue: null,
    };

    analyticsPageApi.refreshWidgetData(
      filterAppliedValues,
      refreshParams,
      successCallback,
      errorCallback
    );
  };

  const filtersHaveLoaded = filterAvailableValues.journeys.length > 0;

  return (
    <div className="px-2 pb-2 mb-12" /* No top padding so filter band sits flush under #top-bar; mb for bottom touch nav */>

      {contentType === "WIDGETS" && (
        <>
          {filterCatalogueError && (
            <AnalyticsPageLoadError
              title={t(
                "Analytics.Page.FiltersLoadFailed",
                "The filters for this page could not be loaded.",
              )}
              help={t(
                "Analytics.Page.LoadFailedHelp",
                "Try again. If it keeps failing, contact support.",
              )}
              onRetry={loadAvailableFilterValues}
            />
          )}
          {!filterCatalogueError &&
            (!filtersHaveLoaded || filterSelectedValues === null) && (
            <AnalyticsLoadingSpinner />
          )}
          {!filterCatalogueError &&
            filtersHaveLoaded &&
            filterSelectedValues !== null && (
            <>
              {shareFilterLoadFailed && (
                <div className="mx-1 mb-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-gray-800">
                  {t(
                    "Analytics.Filters.Saved.ShareLoadFailed",
                    "That shared filter could not be loaded. Showing the page default instead.",
                  )}
                </div>
              )}
              {!savedFilterBootstrapDone && <AnalyticsFilterBandSkeleton />}
              {/* Keep filters mounted while bootstrapping / widgets reload — unmounting reset shareResolvedRef and looped on ?sf=. */}
              <div className={savedFilterBootstrapDone ? undefined : "hidden"}>
                <AnalyticsFilters
                pageIdentifier={pageIdentifier}
                dynamicPageId={dynamicPageId}
                pageTitle={translateTitle ? t(title) : title}
                journeyFilterDefaultValueMode={
                  journeyFilterDefaultValueMode
                }
                journeyFilterRestriction={journeyFilterRestriction}
                availableValues={filterAvailableValues}
                selectedValues={filterSelectedValues}
                appliedValues={filterAppliedValues}
                appliedSavedFilterId={appliedSavedFilterId}
                onAppliedSavedFilterIdChange={setAppliedSavedFilterId}
                onDateRangeChange={handleDateRangeChange}
                onStageFilterInputs={handleStageFilterInputs}
                onActivityWindowDateRangeChange={
                  handleActivityWindowDateRangeChange
                }
                onJourneyPickerChange={handleJourneyPickerChange}
                onLocationPickerChange={handleLocationPickerChange}
                onJobtitlePickerChange={handleJobTitlePickerChange}
                onAppraisalLevelPickerChange={handleAppraisalPickerChange}
                onComparePropertyChange={handleComparePropertyChange}
                onAllJourneysSelectedChange={
                  handleJourneyPickerAllSelectedChange
                }
                onChangeApprovedDataOnly={handleApprovedDataOnlyChange}
                onChangeIncludeLeavers={handleActiveOnlyChange}
                onApplyFilters={onApplyFilters}
                onResetFilters={onResetFilters}
                onDiscardFilters={onDiscardFilters}
                onRecallFilters={onRecallFilters}
                onUserGroupPickerChange={handleUserGroupPickerChange}
                onPointInTimeDateChange={handlePointInTimeDateChange}
                carryFiltersAcrossPages={carryFiltersAcrossPages}
                onCarryFiltersAcrossPagesChange={
                  onCarryFiltersAcrossPagesChange
                }
                // Page default on landing when not restoring a carried session (and not ?sf=).
                applyPageDefaultOnLoad={
                  !carryFiltersAcrossPages ||
                  storedAnalyticFilters == null
                }
                shareSavedFilterId={shareSavedFilterId}
                onShareSavedFilterResolved={(result) => {
                  if (result === "failed") {
                    setShareFilterLoadFailed(true);
                    setSearchParams(
                      (prev) => {
                        const next = new URLSearchParams(prev);
                        next.delete("sf");
                        return next;
                      },
                      { replace: true },
                    );
                  } else {
                    setShareFilterLoadFailed(false);
                  }
                }}
                onSavedFilterBootstrapComplete={(generation) => {
                  if (
                    generation !== savedFilterBootstrapGenerationRef.current
                  ) {
                    return;
                  }
                  setSavedFilterBootstrapDone(true);
                  setFiltersReady(true);
                  // Re-seed carried session from applied refs after page-local bootstrap
                  // (pin/share recall already wrote via onRecallFilters; no-pin Revert needs this).
                  if (carryFiltersRef.current) {
                    persistCarriedFilters(
                      filterAppliedValuesRef.current,
                      appliedSavedFilterIdRef.current,
                    );
                  }
                }}
                savedFilterBootstrapGeneration={savedFilterBootstrapGeneration}
              />
              </div>
              {(!filtersReady || widgetsAreLoading) && (
                <AnalyticsLoadingSpinner />
              )}
              {filtersReady && !widgetsAreLoading && pageDataError && (
                <AnalyticsPageLoadError
                  title={t(
                    "Analytics.Page.LoadFailed",
                    "These charts could not be loaded.",
                  )}
                  help={t(
                    "Analytics.Page.LoadFailedHelp",
                    "Try again. If it keeps failing, contact support.",
                  )}
                  onRetry={reloadData}
                />
              )}
              {filtersReady && !widgetsAreLoading && !pageDataError && (
                <div
                  id="analytics-widgets-container"
                  className={
                    analyticsFiltersAreDirty(
                      filterSelectedValues,
                      filterAppliedValues
                    )
                      ? "opacity-50 transition-opacity duration-150 pointer-events-none"
                      : undefined
                  }
                >
                  {suppressDefaultWidgets ? (
                    children
                  ) : (
                    <>
                      <AnalyticsWidgetCollection
                        widgets={widgets}
                        filters={filterAppliedValues}
                        journeys={filterAvailableValues.journeys}
                        dynamicPageId={dynamicPageId}
                        onRequestOtherWidgetRefresh={
                          handleOtherWidgetRefreshRequest
                        }
                        afterFirstWidget={
                          childrenAfterFirstWidget ? children : undefined
                        }
                      />
                      {!childrenAfterFirstWidget && children !== undefined
                        ? children
                        : null}
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}
      {contentType === "CUSTOM" && <>{children}</>}
      {contentType === "WIDGETS" &&
        allowWalkthrough &&
        savedFilterBootstrapDone &&
        filtersReady &&
        userContext.user.walkthroughSeen != null &&
        !userContext.user.walkthroughSeen.includes("ANALYTICS-FILTERS") &&
        !isMobile && (
          <AnalyticsFiltersWalkthrough
            canManageShared={canManageSharedAnalyticsFilters}
            onFinished={handleWalkthroughFinished}
          />
        )}
      <DynamicAnalyticsEditorMenu pageId={dynamicPageId} />
    </div>
  );
}

export default AnalyticsPage;
